// Synthetic local retention policy only. Plaintext keys remain in memory; session reload
// wrapping uses the current private Auth refresh token, and logout drops the wrapper.
const keys = new Map<string, CryptoKey>();
let retention: number | null = null;
export interface SealedPayload {
  sealed: 1;
  iv: string;
  cipher: string;
  expiresAt: number;
}
const bytes = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const base64 = (b: ArrayBuffer | Uint8Array) => btoa(String.fromCharCode(...new Uint8Array(b)));
const context = (userId: string, organizationId: string) =>
  new TextEncoder().encode(`sqi-oem-vault-v1:${userId}:${organizationId}`);
export function vaultPolicy() {
  if (
    import.meta.env.VITE_OFFLINE_POLICY !== 'synthetic-local-v1' ||
    !/^https?:\/\/(127\.0\.0\.1|localhost)(:\d+)?$/.test(import.meta.env.VITE_API_URL ?? '')
  )
    return null;
  const n = Number(import.meta.env.VITE_OFFLINE_RETENTION_SECONDS);
  return Number.isInteger(n) && n > 0 ? n : null;
}
export async function installVault(userId: string, raw: Uint8Array, seconds: number) {
  if (!Number.isInteger(seconds) || seconds <= 0) throw Error('offline_policy_unconfigured');
  for (const identity of keys.keys()) if (identity !== userId) forgetVault(identity);
  retention = seconds;
  keys.set(
    userId,
    await crypto.subtle.importKey('raw', raw as BufferSource, 'AES-GCM', false, [
      'encrypt',
      'decrypt',
    ]),
  );
}
async function wrappingKey(refreshToken: string) {
  return crypto.subtle.importKey(
    'raw',
    await crypto.subtle.digest('SHA-256', new TextEncoder().encode(refreshToken)),
    'AES-GCM',
    false,
    ['encrypt', 'decrypt'],
  );
}
export async function retainVaultKey(userId: string, raw: string, refreshToken: string) {
  const policy = vaultPolicy();
  if (!policy) return;
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: context(userId, 'auth-session') },
    await wrappingKey(refreshToken),
    bytes(raw),
  );
  sessionStorage.setItem(
    `sqi-vault:${userId}`,
    JSON.stringify({ iv: base64(iv), cipher: base64(cipher) }),
  );
  await installVault(userId, bytes(raw), policy);
}
export async function restoreVaultKey(userId: string, refreshToken: string) {
  const policy = vaultPolicy(),
    stored = sessionStorage.getItem(`sqi-vault:${userId}`);
  if (!policy || !stored) return false;
  try {
    const v = JSON.parse(stored) as { iv: string; cipher: string };
    const raw = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: bytes(v.iv), additionalData: context(userId, 'auth-session') },
      await wrappingKey(refreshToken),
      bytes(v.cipher),
    );
    await installVault(userId, new Uint8Array(raw), policy);
    return true;
  } catch {
    return false;
  }
}
export function forgetVault(userId: string) {
  keys.delete(userId);
  sessionStorage.removeItem(`sqi-vault:${userId}`);
}
export function isSealed(value: unknown): value is SealedPayload {
  return Boolean(value && typeof value === 'object' && (value as SealedPayload).sealed === 1);
}
export async function sealPayload(
  userId: string,
  organizationId: string,
  value: unknown,
): Promise<SealedPayload> {
  if (isSealed(value)) return value;
  const key = keys.get(userId);
  if (!key || !retention) throw Error('offline_identity_sealed');
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: context(userId, organizationId) },
    key,
    new TextEncoder().encode(JSON.stringify(value)),
  );
  return {
    sealed: 1,
    iv: base64(iv),
    cipher: base64(cipher),
    expiresAt: Date.now() + retention * 1000,
  };
}
export async function openPayload<T = unknown>(
  userId: string,
  organizationId: string,
  value: unknown,
): Promise<T> {
  const key = keys.get(userId);
  if (!key) throw Error('offline_identity_sealed');
  if (!isSealed(value)) throw Error('unsealed_legacy_requires_review');
  if (Date.now() >= value.expiresAt) throw Error('offline_retention_expired');
  const clear = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: bytes(value.iv), additionalData: context(userId, organizationId) },
    key,
    bytes(value.cipher),
  );
  return JSON.parse(new TextDecoder().decode(clear)) as T;
}
